/**
 * The page editor's controller hook (plan 3.3, 7.3), shared by the web
 * iframe surface (`PageEditor.web.tsx`) and the native WebView surface
 * (`PageEditor.tsx`): loads `editor-source` for the head, owns the session
 * token, the op queue and the bridge session, reloads after a duplicate, a
 * refused save or Reload, flushes when the app goes to the background and
 * when editing ends, and registers the editor for the suggestions card.
 *
 * The surface gives it a way to post to the frame (`post`) and, on web, the
 * frame's window (`expectedSource`); it hands every frame message to
 * `receive`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PageFinding } from '@tabbio/types';
import type { ArtifactEditorParentMessage } from '@tabbio/utils/artifact-editor-bridge';
import { getEditorSource, savePageEdits } from '@/lib/artifact-edits-api';
import { useInvalidateArtifact } from '@/hooks/useArtifactVersions';
import { notePageSave } from '../suggestions/suggestions-store';
import { registerActiveEditor } from './active-editor';
import { createEditorToken, createUuid } from './editor-ids';
import { PageEditQueue } from './page-edit-queue';
import {
  INITIAL_PAGE_EDITOR_SNAPSHOT,
  PageEditorSession,
  type PageEditorSnapshot,
} from './page-editor-session';
import type { EditorSaveState } from './save-state';

export type PageEditorLoad =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; versionId: string; html: string; generation: number };

export type PageEditorChecks = {
  findings: PageFinding[];
  warnings: PageFinding[];
  removed: string[];
};

export type PageEditorController = {
  load: PageEditorLoad;
  token: string;
  snapshot: PageEditorSnapshot;
  saveState: EditorSaveState;
  /** Findings of the newest save (null before the first save of the session). */
  checks: PageEditorChecks | null;
  session: PageEditorSession | null;
  /** Hand a frame message over (`event.data`, and `event.source` on web). */
  receive: (data: unknown, source?: unknown) => void;
  /** Send everything now; resolves false when a save failed. */
  flush: () => Promise<boolean>;
  retry: () => void;
  /** Load the head again (Reload after a conflict). */
  reload: () => void;
  /** The version the next save is made against. */
  baseVersionId: () => string | null;
};

export function usePageEditorController(options: {
  artifactId: string;
  /** Posts a message to the frame. */
  post: (message: ArtifactEditorParentMessage) => void;
  /** Web: the frame's window, which must have posted every message. */
  expectedSource?: () => unknown;
  /** The brand accent for the bridge's outline. */
  accent?: string;
  /**
   * Watches the app going to the background (web `visibilitychange`,
   * native `AppState`); returns the unsubscribe.
   */
  onBackground?: (flush: () => void) => () => void;
}): PageEditorController {
  const { artifactId } = options;
  const invalidate = useInvalidateArtifact();
  const [load, setLoad] = useState<PageEditorLoad>({ status: 'loading' });
  const [snapshot, setSnapshot] = useState<PageEditorSnapshot>(INITIAL_PAGE_EDITOR_SNAPSHOT);
  const [saveState, setSaveState] = useState<EditorSaveState>({ status: 'idle' });
  const [checks, setChecks] = useState<PageEditorChecks | null>(null);
  const [session, setSession] = useState<PageEditorSession | null>(null);
  const token = useMemo(() => createEditorToken(), []);
  const editSessionId = useMemo(() => createUuid(), []);
  const postRef = useRef(options.post);
  postRef.current = options.post;
  const sourceRef = useRef(options.expectedSource);
  sourceRef.current = options.expectedSource;
  const queueRef = useRef<PageEditQueue | null>(null);
  const sessionRef = useRef<PageEditorSession | null>(null);
  const generation = useRef(0);
  const alive = useRef(true);

  const fetchSource = useCallback(async () => {
    setLoad({ status: 'loading' });
    try {
      const source = await getEditorSource(artifactId);
      if (!alive.current) return;
      generation.current += 1;
      queueRef.current?.reset(source.versionId);
      sessionRef.current?.reset();
      setLoad({ status: 'ready', versionId: source.versionId, html: source.html, generation: generation.current });
    } catch {
      if (alive.current) setLoad({ status: 'error' });
    }
  }, [artifactId]);

  // One queue and one session per mount (one editing session).
  useEffect(() => {
    alive.current = true;
    const queue = new PageEditQueue('', editSessionId, {
      save: (request) => savePageEdits(artifactId, request),
      onSaved: (result, _sent, baseVersionId) => {
        sessionRef.current?.saved(result);
        // Suggestions made on the base follow the page (renumbered ids).
        notePageSave(artifactId, baseVersionId, result.version.id, result.ids);
        if (alive.current) {
          setChecks({ findings: result.findings ?? [], warnings: result.warnings ?? [], removed: result.removed ?? [] });
        }
        // A new version shows in the version control; squashed saves wait for Done.
        if (!result.squashed) invalidate(artifactId);
      },
      onReloadNeeded: () => {
        void fetchSource();
      },
      onStateChange: (state) => {
        if (alive.current) setSaveState(state);
      },
    });
    queueRef.current = queue;
    const created = new PageEditorSession({
      token,
      queue,
      post: (message) => postRef.current(message),
      expectedSource: sourceRef.current ? () => sourceRef.current?.() : undefined,
      onChange: (next) => {
        if (alive.current) setSnapshot(next);
      },
    });
    created.setMode('edit', options.accent);
    sessionRef.current = created;
    setSession(created);
    void fetchSource();
    return () => {
      alive.current = false;
      queueRef.current = null;
      sessionRef.current = null;
      // Leaving edit mode or closing the panel sends what is left.
      void queue.flush().finally(() => {
        queue.dispose();
        invalidate(artifactId);
      });
    };
    // One editing session per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactId]);

  // The app going to the background sends what is queued.
  const onBackground = options.onBackground;
  useEffect(() => {
    if (!onBackground) return;
    return onBackground(() => {
      void queueRef.current?.flush();
    });
  }, [onBackground]);

  // The suggestions card applies through this editor while it is open.
  useEffect(() => {
    if (load.status !== 'ready') return;
    const versionId = load.versionId;
    return registerActiveEditor({
      artifactId,
      kind: 'page',
      baseVersionId: () => queueRef.current?.baseVersionId || versionId,
      applyPageOps: (ops) => {
        // The batch carrying a suggestion is counted as `suggest-apply`.
        queueRef.current?.markNextSave('suggestion');
        sessionRef.current?.applyOps(ops);
      },
      reveal: (target) => sessionRef.current?.focus(target),
    });
  }, [artifactId, load]);

  const receive = useCallback((data: unknown, source?: unknown) => {
    sessionRef.current?.receive(data, source);
  }, []);
  const flush = useCallback(() => queueRef.current?.flush() ?? Promise.resolve(true), []);
  const retry = useCallback(() => {
    void queueRef.current?.retry();
  }, []);
  const reload = useCallback(() => {
    void fetchSource();
  }, [fetchSource]);
  const baseVersionId = useCallback(
    () => queueRef.current?.baseVersionId || (load.status === 'ready' ? load.versionId : null),
    [load],
  );

  return { load, token, snapshot, saveState, checks, session, receive, flush, retry, reload, baseVersionId };
}
